import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type {
  CreditCardBillPaymentFormData,
  CreditCardBill,
  CreditCardBillUpdateFormData,
  CreditCardOverview,
  CreditCardProfile,
  CreditCardProfileEntry,
  CreditCardProfileFormData,
  CreditCardStatus,
} from "@/types";

/**
 * Query keys for the credit-cards domain. Month-scoped keys refetch when the
 * billing-period selector changes.
 */
export const creditCardKeys = {
  all: ["credit-cards"] as const,
  overview: (month: string, status: CreditCardStatus) =>
    [...creditCardKeys.all, "overview", month, status] as const,
  bills: (startMonth: string, endMonth: string, status: CreditCardStatus) =>
    [...creditCardKeys.all, "bills", startMonth, endMonth, status] as const,
  profiles: (status: CreditCardStatus) => [...creditCardKeys.all, "profiles", status] as const,
};

/** Aggregated overview for a billing month (defaults to current month). */
export function useCreditCardOverview(month: string, status: CreditCardStatus = "active") {
  return useQuery({
    queryKey: creditCardKeys.overview(month, status),
    queryFn: () => apiClient.getCreditCardOverview(month || undefined, status),
  });
}

/** Card × month bills matrix for a month range. */
export function useCreditCardBills(
  startMonth: string,
  endMonth: string,
  status: CreditCardStatus = "active",
) {
  return useQuery({
    queryKey: creditCardKeys.bills(startMonth, endMonth, status),
    queryFn: () => apiClient.getCreditCardBills(startMonth, endMonth, status),
    enabled: !!startMonth && !!endMonth && startMonth <= endMonth,
  });
}

/** Card knowledge-base profiles (cards without a profile return null). */
export function useCreditCardProfiles(status: CreditCardStatus = "active") {
  return useQuery({
    queryKey: creditCardKeys.profiles(status),
    queryFn: () => apiClient.getCreditCardProfiles(status),
  });
}

export function useUpsertCreditCardProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, data }: { accountId: number; data: CreditCardProfileFormData }) =>
      apiClient.upsertCreditCardProfile(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: creditCardKeys.all });
      toast({
        title: "Success",
        description: "Card profile saved",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save card profile",
        variant: "destructive",
      });
    },
  });
}

export function useRecordCreditCardBillPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, data }: { billId: number; data: CreditCardBillPaymentFormData }) =>
      apiClient.recordCreditCardBillPayment(billId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: creditCardKeys.all });
      toast({ title: "Payment recorded" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to record payment",
        variant: "destructive",
      });
    },
  });
}

export function useUpsertCreditCardBill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      month,
      data,
    }: {
      accountId: number;
      month: string;
      data: Partial<CreditCardBillUpdateFormData>;
    }) => apiClient.upsertCreditCardBill(accountId, month, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: creditCardKeys.all });
      toast({
        title: "Success",
        description: "Bill saved",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save bill",
        variant: "destructive",
      });
    },
  });
}

export type { CreditCardBill, CreditCardOverview, CreditCardProfile, CreditCardProfileEntry };
